import { ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import { unitLabel } from '@/lib/legUnits'
import { isSuppressed } from '@/types/domain'
import type { CaseLeg, CaseMoney, ExpenseLine, Settlement } from '@/types/domain'

import { OVERRUN_TONE, signedNumber, varianceTone } from './money'

/** The tone each served paid status wears (S-4.2) — the documents table's
 *  for the same states (`DOC_STATUS_TONES` in CaseDocuments): nothing paid is
 *  neutral, part of it is attention, all of it is positive. Never brand: blue
 *  is identity and in-progress, and a payment state is neither.
 *
 *  Tones only, never words: a module constant is evaluated once at import, and
 *  a word baked in here would freeze in whichever language was active then. */
const SETTLEMENT_TONES: Record<Settlement, 'neutral' | 'attention' | 'positive'> = {
  unpaid: 'neutral',
  'partly-paid': 'attention',
  paid: 'positive',
}

/** The plan against the fact, line by line: one row per folded cost line, and
 *  the served column sums under them.
 *
 *  Takes the block rather than the expense array, because the totals row is not
 *  arithmetic over the rows — see the footer's own note.
 *
 *  ── Detail groups ──────────────────────────────────────────────────────────
 *  A planned line may name another as the generalized plan it details
 *  (`detailOf`, spec 2026-09-01-expense-detail-grouping L1–L4): the quote
 *  carries «Transportation service 8 709» and the rates actually agreed per
 *  truck detail it. The server stops counting a detailed parent's own plan the
 *  moment one detail exists, so this table must not show it as a line either —
 *  the parent becomes a collapsed group whose three figures are its details',
 *  and its quote figure moves under the name as the benchmark it now is. The
 *  grouping is composed HERE from the flat list; no group aggregate is served
 *  and none is invented, only served per-row figures added up.
 */
export function ExpenseTable({
  caseId,
  legs,
  money,
  onEdit,
  onDelete,
  onOpenLeg,
  pending,
}: {
  caseId: string
  /** The case's route, for the direction column. The rows carry a `legId` and
   *  a `unitId` (FR-67); the SEQUENCE and the UNIT those resolve to live on
   *  the legs, and this table joins the two rather than asking the server for a
   *  label it can compose here — the same division `AgedItem` keeps, where the
   *  server serves the id and the route is the client's to build. */
  legs: CaseLeg[]
  money: CaseMoney
  onEdit: (expense: ExpenseLine) => void
  /** Drops the whole line — the plan and every actual against it, which is what
   *  the row's own confirmation says out loud before it calls this. */
  onDelete: (expense: ExpenseLine) => void
  /** Opens the drawer about the leg a line is attributed to (S-11). When
   *  given, the «Плечо N» cell is a button that calls it; without it the cell
   *  stays a link to the leg's page. */
  onOpenLeg?: (expense: ExpenseLine, leg: CaseLeg) => void
  /** A money mutation is in flight; the row acts go quiet rather than queueing
   *  a second delete behind the first. */
  pending?: boolean
}) {
  const { t } = useTranslation()
  const currency = money.rate.currency
  const legById = new Map(legs.map((leg) => [leg.id, leg]))

  const byId = new Map(money.expenses.map((expense) => [expense.id, expense]))
  /* Details by parent id. A detail whose parent this payload does not carry —
     deleted in another tab, 0060's ON DELETE SET NULL not yet reached this
     cached copy — stays a top-level row rather than vanishing into a group
     nothing renders. The same forgiveness `LegLink` shows a leg the route has
     lost, and for the same reason: the two facts are composed separately. */
  const details = new Map<string, ExpenseLine[]>()
  for (const expense of money.expenses) {
    if (expense.detailOf !== undefined && byId.has(expense.detailOf)) {
      details.set(expense.detailOf, [
        ...(details.get(expense.detailOf) ?? []),
        expense,
      ])
    }
  }
  const topLevel = money.expenses.filter(
    (expense) => expense.detailOf === undefined || !byId.has(expense.detailOf),
  )

  /* Collapsed by default, and deliberately not persisted: the group exists to
     keep six truck rates out of the way of the eight lines that are not
     trucks, so every visit starts from the summary rather than from wherever
     the last reader left it. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <div className="asa-scroll mt-3 overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            <th scope="col" className="asa-eyebrow h-9 px-[18px]">{t('cases.money.columns.line')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3">{t('cases.money.columns.vendor')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3">{t('cases.money.columns.leg')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.plannedIn', { currency })}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.actualIn', { currency })}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.variance')}</th>
            {/* Right-set like the acts under it, and named rather than left as
                a bare column: a heading-less trailing column reads as part of
                the variance beside it. */}
            <th scope="col" className="asa-eyebrow h-9 px-[18px] text-right">{t('cases.money.columns.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {topLevel.map((expense) => {
            const children = details.get(expense.id)
            if (children === undefined) {
              return (
                <ExpenseRow
                  key={expense.id}
                  caseId={caseId}
                  expense={expense}
                  legs={legById}
                  columnCurrency={currency}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onOpenLeg={onOpenLeg}
                  pending={pending}
                />
              )
            }

            const open = expanded.has(expense.id)
            const group = groupFigures(expense, children)

            return (
              <ExpenseRow
                key={expense.id}
                caseId={caseId}
                expense={expense}
                legs={legById}
                columnCurrency={currency}
                onEdit={onEdit}
                onDelete={onDelete}
                onOpenLeg={onOpenLeg}
                pending={pending}
                figures={group}
                /* The chevron is the ONLY thing that folds. The rest of the
                   row keeps the act every other row has — it opens the parent
                   for editing — so the press has to be stopped here rather
                   than the row's own handler being weakened for the group. */
                lead={
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={t('cases.money.toggleGroup', {
                      line: expense.description,
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                      toggle(expense.id)
                    }}
                    className="text-text-muted hover:text-text-heading mr-1.5 inline-flex align-middle"
                  >
                    <ChevronRight
                      size={14}
                      className={cn('transition-transform', open && 'rotate-90')}
                    />
                  </button>
                }
                /* The quote figure and how far the contracted rates have moved
                   from it. Across currencies there is no delta to state —
                   subtracting CNY from USD would be the one piece of money
                   arithmetic this client must never do — so the note falls
                   back to the count alone. */
                subnote={
                  group.uniform
                    ? t('cases.money.groupNote', {
                        quota: formatNumber(expense.planned.amount, 2),
                        delta: signedNumber(
                          String(group.planned - Number(expense.planned.amount)),
                        ),
                        count: children.length,
                      })
                    : t('cases.money.groupNoteMixed', { count: children.length })
                }
              >
                {open
                  ? children.map((child) => (
                      <ExpenseRow
                        key={child.id}
                        caseId={caseId}
                        expense={child}
                        legs={legById}
                        columnCurrency={currency}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onOpenLeg={onOpenLeg}
                        pending={pending}
                        indent
                      />
                    ))
                  : null}
              </ExpenseRow>
            )
          })}
        </tbody>
        {/* Column sums are SERVED (AD-9), and deliberately not the
            header's actualCost: the header forecasts the whole case,
            this row totals only what has actually been recorded. */}
        <tfoot>
          <tr className="border-line border-t-2">
            <td colSpan={3} className="text-text-muted px-3 py-3 text-right text-[13px]">
              {t('cases.money.totals')}
            </td>
            <td className="asa-num text-text-heading px-3 py-3 text-right text-[13px] font-semibold">
              {formatNumber(money.plannedCost.amount, 2)}
            </td>
            <td className="asa-num text-text-heading px-3 py-3 text-right text-[13px] font-semibold">
              {money.actualToDate ? formatNumber(money.actualToDate.amount, 2) : '—'}
            </td>
            <td
              className={cn(
                'asa-num px-3 py-3 text-right text-[13px]',
                money.costVariance
                  ? varianceTone(money.costVariance.amount)
                  : 'text-text-muted',
              )}
            >
              {money.costVariance ? signedNumber(money.costVariance.amount) : '—'}
            </td>
            {/* Nothing to act on in a totals row; the cell exists so the
                column count matches the header. */}
            <td className="px-[18px]" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** What a group shows instead of its parent's own three figures.
 *
 *  Addition over figures the server already converted and sent, which is the
 *  whole of the licence this client has (AD-9): the footer totals stay served.
 *  The parent's OWN actual is added in because a retro invoice against the
 *  generalized line is still money spent — the server counts it in
 *  ActualToDate for exactly that reason — while its plan is not, because the
 *  details replaced it.
 *
 *  `uniform` guards every sum this function makes, and the delta the note
 *  states: the planned, actual and variance currency of every detail, and the
 *  parent's own actual, must all be the parent's plan currency. It used to
 *  compare the PLANNED currencies only, which was enough while an actual was
 *  always booked in its plan's currency; once each side has its own (S-4.1),
 *  a group planned in USD and invoiced partly in CNY would add the two. When
 *  they are not uniform, no figure is shown at all — never a sum across
 *  currencies (AD-9). */
function groupFigures(parent: ExpenseLine, rows: ExpenseLine[]) {
  const sum = (pick: (row: ExpenseLine) => string | undefined) =>
    rows.reduce((total, row) => total + Number(pick(row) ?? 0), 0)

  const hasActual =
    parent.actual !== undefined || rows.some((row) => row.actual !== undefined)
  const hasVariance = rows.some((row) => row.variance !== undefined)

  const currency = parent.planned.currency
  const currencies = [
    parent.actual?.currency,
    ...rows.flatMap((row) => [row.planned.currency, row.actual?.currency, row.variance?.currency]),
  ]

  return {
    currency,
    uniform: currencies.every((code) => code === undefined || code === currency),
    planned: sum((row) => row.planned.amount),
    actual: hasActual
      ? sum((row) => row.actual?.amount) + Number(parent.actual?.amount ?? 0)
      : undefined,
    variance: hasVariance ? sum((row) => row.variance?.amount) : undefined,
  }
}

/** One cost line's `<tr>` — a plain row, a group's parent, or a detail inside
 *  an open group. One component for all three so the three cannot drift: the
 *  parent adds a `lead` (the chevron) and a `subnote`, a detail indents, and
 *  the group replaces the three figures with `figures`. Everything else — the
 *  edit affordance, the attention wash, the direction link — is the same row
 *  it has always been.
 *
 *  `children` are the detail rows, rendered as siblings AFTER this `<tr>`: a
 *  row cannot nest a row, and a fragment keeps them in the one `<tbody>`. */
function ExpenseRow({
  caseId,
  expense,
  legs,
  columnCurrency,
  onEdit,
  onDelete,
  onOpenLeg,
  pending,
  lead,
  subnote,
  figures,
  indent,
  children,
}: {
  caseId: string
  expense: ExpenseLine
  legs: Map<string, CaseLeg>
  /** The currency the column headers name (the case rate's). A figure in any
   *  other says which, beside itself. */
  columnCurrency: string
  onEdit: (expense: ExpenseLine) => void
  onDelete: (expense: ExpenseLine) => void
  onOpenLeg?: (expense: ExpenseLine, leg: CaseLeg) => void
  pending?: boolean
  lead?: ReactNode
  subnote?: ReactNode
  figures?: ReturnType<typeof groupFigures>
  indent?: boolean
  children?: ReactNode
}) {
  const { t } = useTranslation()

  /* The delete asks first, IN THE ROW, and the second press carries the word
     rather than the icon — the same shape the sheet's own DeleteRow uses, and
     for the same reason it exists at all: this drops the plan AND every actual
     recorded against it, and it now sits one press away from a pencil. An
     icon that deleted on the first press would put the dangerous act on the
     safe act's muscle memory, which is precisely what the icon rule and
     FormSheet's header both refuse. */
  const [confirming, setConfirming] = useState(false)

  /* A booking note and a pending pass-through can coexist;
     either way the line explains itself under its own name. A note withheld
     from this reader (AD-6 — it can name the vendor) prints nothing: the
     vendor cell already says it is hidden, and the marker joined as text
     read «[object Object]». */
  const note = [
    isSuppressed(expense.note) ? undefined : expense.note,
    expense.pendingClientApproval ? t('cases.money.passThroughPending') : null,
  ]
    .filter(Boolean)
    .join(' — ')

  /* A mixed-currency group shows NO figures at all: its sums would be the one
     piece of money arithmetic this client must never do (AD-9), and a number
     over rows in two currencies reads as an amount somebody computed. The
     expanded rows carry the honest per-line figures. */
  /* An UNPLANNED line's plan is a dash, never the placeholder zero the DTO
     serves for it (S-4.1): «0.00» would say "planned to cost nothing", and
     nobody planned it at all. */
  const planned = figures
    ? figures.uniform
      ? String(figures.planned)
      : undefined
    : expense.unplanned
      ? undefined
      : expense.planned.amount
  const actual = figures
    ? figures.uniform && figures.actual !== undefined
      ? String(figures.actual)
      : undefined
    : expense.actual?.amount
  const variance = figures
    ? figures.uniform && figures.variance !== undefined
      ? String(figures.variance)
      : undefined
    : expense.variance?.amount

  /* The currency each figure is in. A group's three are its plan currency —
     `uniform` is what licensed adding them up. A row's are its own sides': an
     actual invoiced in CNY under a USD plan, and a variance served in USD when
     the two differ (from the booked equivalents, AD-9). */
  const plannedCurrency = figures ? figures.currency : expense.planned.currency
  const actualCurrency = figures ? figures.currency : expense.actual?.currency
  const varianceCurrency = figures ? figures.currency : expense.variance?.currency

  /* Every unit of a cost nobody planned is overrun, so an unplanned line wears
     the overrun tone whatever sign its served variance has. */
  const varianceClass =
    variance === undefined
      ? 'text-text-muted'
      : expense.unplanned && !figures
        ? OVERRUN_TONE
        : varianceTone(variance)

  return (
    <>
      <tr
        className={cn(
          'border-line-hair cursor-pointer border-b transition-colors last:border-b-0',
          /* The undecided pass-through holds its whole row on the
             attention wash — the same yellow every surface uses
             for "waiting on someone", so the table itself says
             which line the Super-admin is sitting on. */
          expense.pendingClientApproval ? 'bg-attention-soft' : 'hover:bg-surface-hover',
        )}
        onClick={() => onEdit(expense)}
        tabIndex={0}
        aria-label={t('cases.money.editExpense')}
        onKeyDown={(event) => {
          /* Only the ROW's own keys open the edit. The chevron is a real
             button inside this cell, and its Enter/Space bubble up here —
             without this guard one press would both fold the group and open
             the parent's form, and preventDefault would eat the button's
             own activation. */
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onEdit(expense)
          }
        }}
      >
        <td className={cn('py-2.5 align-top', indent ? 'pr-3 pl-9' : 'px-[18px]')}>
          {lead}
          <span className="text-text-heading text-[13px]">{expense.description}</span>
          {/* Said in words, beside the name: the dash in the plan column and the
              red variance would otherwise carry the meaning by shape and colour
              alone. */}
          {expense.unplanned ? (
            <Tag className="ml-2 align-middle">{t('cases.money.unplanned')}</Tag>
          ) : null}
          {/* How far the cost is paid (S-4.2), as the server derived it from
              the payments allocated to it — read, never worked out here. Absent
              on a plan with no actual, and then nothing is said: "unpaid" would
              claim a debt nobody has invoiced. Never on a GROUP row, whose
              figures are its details': one word there would be one status for
              several debts, each of which says its own when the group opens.
              A word this tab does not know (a newer server) says nothing
              rather than printing its catalogue key. */}
          {!figures && expense.settlement && SETTLEMENT_TONES[expense.settlement] ? (
            <Tag tone={SETTLEMENT_TONES[expense.settlement]} className="ml-2 align-middle">
              {t(`cases.money.settlement.${expense.settlement}`)}
            </Tag>
          ) : null}
          {subnote ? (
            <span className="text-text-muted mt-0.5 block max-w-72 text-[12px] leading-snug font-light">
              {subnote}
            </span>
          ) : null}
          {note ? (
            <span
              className={cn(
                'mt-0.5 block max-w-72 text-[12px] leading-snug font-light',
                expense.pendingClientApproval ? 'text-attention' : 'text-text-muted',
              )}
            >
              {note}
            </span>
          ) : null}
        </td>
        <td className="text-text-muted px-3 py-2.5 align-top text-[12.5px]">
          <SuppressedValue
            value={expense.counterpartyName}
            label={t('common.suppressed.vendor')}
          />
        </td>
        {/* What this cost paid for — a link to the direction's own
            page, with the truck when the row names one (FR-67).
            A cost that belongs to no single movement says so in words
            rather than leaving the cell blank: a clearance fee or a
            bank charge is legitimately unattributed, and an empty cell
            would read as a row somebody forgot to file.

            A GROUP leaves it blank instead: its details sit on different
            directions and the group has no single one, which the expanded
            rows say for themselves. */}
        <td className="px-3 py-2.5 align-top text-[12.5px]">
          {figures ? null : (
            <LegLink caseId={caseId} expense={expense} legs={legs} onOpen={onOpenLeg} />
          )}
        </td>
        <td className="asa-num text-text-body px-3 py-2.5 text-right align-top text-[13px]">
          {planned === undefined ? (
            '—'
          ) : (
            <Figure
              text={formatNumber(planned, 2)}
              currency={plannedCurrency}
              columnCurrency={columnCurrency}
            />
          )}
        </td>
        <td className="asa-num text-text-body px-3 py-2.5 text-right align-top text-[13px]">
          {actual === undefined ? (
            '—'
          ) : (
            <Figure
              text={formatNumber(actual, 2)}
              currency={actualCurrency}
              columnCurrency={columnCurrency}
            />
          )}
        </td>
        <td
          className={cn(
            'asa-num px-[18px] py-2.5 text-right align-top text-[13px]',
            varianceClass,
          )}
        >
          {variance === undefined ? (
            '—'
          ) : (
            <Figure
              text={signedNumber(variance)}
              currency={varianceCurrency}
              columnCurrency={columnCurrency}
            />
          )}
        </td>
        {/* The row's own acts. Every press stops here: the whole row opens the
            edit sheet, so a click that reached it would run two acts at once —
            and a delete confirmation dismissed by the sheet it opened would be
            unusable. */}
        <td
          className="px-[18px] py-2.5 text-right align-top whitespace-nowrap"
          onClick={(event) => event.stopPropagation()}
        >
          {confirming ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-text-body text-[12.5px] leading-snug">
                {t('cases.money.deleteExpenseConfirm')}
              </span>
              <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false)
                  onDelete(expense)
                }}
              >
                {t('cases.money.deleteExpenseConfirmAction')}
              </Button>
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5">
              <IconButton
                size="sm"
                icon={Pencil}
                disabled={pending}
                label={t('cases.money.editExpenseNamed', { line: expense.description })}
                onClick={() => onEdit(expense)}
              />
              <IconButton
                size="sm"
                icon={Trash2}
                disabled={pending}
                className="hover:text-negative"
                label={t('cases.money.deleteExpenseNamed', { line: expense.description })}
                onClick={() => setConfirming(true)}
              />
            </span>
          )}
        </td>
      </tr>
      {children}
    </>
  )
}

/** A figure, and its currency code when that is not the column's.
 *
 *  The headers name one currency — «Факт USD» — and a bare 7 000.00 under it
 *  reads as dollars. A plan and its invoice may now be in different currencies
 *  (S-4.1), so a figure that is not in the header's says which it is in. The
 *  code is muted and light: it qualifies the number, it does not compete with
 *  it. A figure in the header's currency stays bare, as every row did. */
function Figure({
  text,
  currency,
  columnCurrency,
}: {
  text: string
  currency: string | undefined
  columnCurrency: string
}) {
  if (currency === undefined || currency === columnCurrency) return <>{text}</>
  return (
    <>
      {text}
      <span className="text-text-muted text-[11.5px] font-light">{`\u00a0${currency}`}</span>
    </>
  )
}

/** Which movement a cost paid for — «Плечо 2 · 63AJN05», linked.
 *
 *  The row's own act is EDIT (the whole row is clickable), so this link stops
 *  the click from reaching it: a reader pressing a link means the link, and a
 *  sheet opening over the page they asked to navigate to is the sort of thing
 *  that teaches people not to press it again.
 *
 *  On the case's «Деньги» (S-11) the cell is a BUTTON instead (`onOpen`): it
 *  opens the drawer about that leg's carrier money, which leads to the leg's
 *  page itself. Same label, the same stopped press, and `aria-haspopup` so a
 *  screen reader hears that it opens a dialog rather than navigating. The
 *  row's own keyboard guard already ignores keys that start on it.
 *
 *  A `legId` naming a leg this payload does not carry falls back to the
 *  unattributed wording rather than linking to a page that would 404. That is
 *  not defensive noise: the money block and the route are composed from two
 *  modules, and a cost on a leg somebody deleted in another tab is exactly the
 *  state this cell can be handed. */
function LegLink({
  caseId,
  expense,
  legs,
  onOpen,
}: {
  caseId: string
  expense: ExpenseLine
  legs: Map<string, CaseLeg>
  onOpen?: (expense: ExpenseLine, leg: CaseLeg) => void
}) {
  const { t } = useTranslation()

  const leg = expense.legId ? legs.get(expense.legId) : undefined
  if (!leg) {
    return <span className="text-text-muted font-light">{t('cases.money.unattributed')}</span>
  }

  /* The unit by kind and number — «Плечо 1 · Контейнер MSKU…» (S-18b). */
  const unit = expense.unitId ? leg.units?.find((candidate) => candidate.id === expense.unitId) : undefined

  const label = unit
    ? t('cases.money.legLinkUnit', { n: leg.sequence, unit: unitLabel(t, unit) })
    : t('cases.money.legLink', { n: leg.sequence })

  if (onOpen) {
    return (
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={(event) => {
          event.stopPropagation()
          onOpen(expense, leg)
        }}
        className="text-text-link hover:text-text-link-hover cursor-pointer text-left"
      >
        {label}
      </button>
    )
  }

  return (
    <Link
      to={`/cases/${caseId}/legs/${leg.id}`}
      onClick={(event) => event.stopPropagation()}
      className="text-text-link hover:text-text-link-hover"
    >
      {label}
    </Link>
  )
}
